import type { IconType } from 'react-icons';
import { GoPeople } from 'react-icons/go';
import { IoClose } from 'react-icons/io5';

import { ButtonSmall } from '@/components/Button';
import { Input, PhoneInput } from '@/components/Inputs';

import {
  dayWorkerContactError,
  dayWorkerEmailError,
  dayWorkerNameError,
  toDailyRateInput,
} from '../dayWorkerValidation';
import type { DayWorkerRow } from '../types';

import s from './DayWorkersSection.module.scss';
import { SectionHeader } from './SectionHeader';

interface DayWorkersSectionProps {
  workers: DayWorkerRow[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<DayWorkerRow>) => void;
  onRemove: (id: string) => void;
  icon?: IconType;
}

export function DayWorkersSection({
  workers,
  onAdd,
  onUpdate,
  onRemove,
  icon: Icon = GoPeople,
}: DayWorkersSectionProps) {
  return (
    <div className={s.section}>
      <SectionHeader icon={Icon} title="Day Workers" />

      <div className={s.tableWrapper}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact #</th>
              <th>Daily Rate</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {workers.map((worker) => (
              <tr key={worker.id}>
                <td>
                  <Input
                    type="text"
                    value={worker.name}
                    onChange={(e) => onUpdate(worker.id, { name: e.target.value })}
                    placeholder="Name"
                    error={dayWorkerNameError(worker)}
                  />
                </td>
                <td>
                  <PhoneInput
                    id={`day-worker-contact-${worker.id}`}
                    value={worker.contact}
                    onChange={(contact) => onUpdate(worker.id, { contact: contact ?? '' })}
                    error={dayWorkerContactError(worker.contact)}
                  />
                </td>
                <td>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={worker.dailyRate}
                    onChange={(e) =>
                      onUpdate(worker.id, { dailyRate: toDailyRateInput(e.target.value) })
                    }
                    placeholder="Rate"
                  />
                </td>
                <td>
                  <Input
                    type="email"
                    value={worker.email}
                    onChange={(e) => onUpdate(worker.id, { email: e.target.value })}
                    placeholder="Email"
                    error={dayWorkerEmailError(worker.email)}
                  />
                </td>
                <td className={s.actionCell}>
                  <button
                    className={s.removeBtn}
                    onClick={() => onRemove(worker.id)}
                    title="Remove Day Worker"
                  >
                    <IoClose />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={s.mobileList}>
        {workers.map((worker) => (
          <div key={worker.id} className={s.mobileCard}>
            <div className={s.mobileCardTopRow}>
              <button
                className={s.removeBtn}
                onClick={() => onRemove(worker.id)}
                title="Remove Day Worker"
              >
                <IoClose />
              </button>
            </div>
            <div className={s.mobileInputGrid}>
              <Input
                type="text"
                label="Name"
                value={worker.name}
                onChange={(e) => onUpdate(worker.id, { name: e.target.value })}
                placeholder="Name"
                error={dayWorkerNameError(worker)}
              />
              <PhoneInput
                id={`day-worker-contact-mobile-${worker.id}`}
                label="Contact #"
                value={worker.contact}
                onChange={(contact) => onUpdate(worker.id, { contact: contact ?? '' })}
                error={dayWorkerContactError(worker.contact)}
              />
              <Input
                type="text"
                inputMode="decimal"
                label="Daily Rate"
                value={worker.dailyRate}
                onChange={(e) =>
                  onUpdate(worker.id, { dailyRate: toDailyRateInput(e.target.value) })
                }
                placeholder="Rate"
              />
              <Input
                type="email"
                label="Email"
                value={worker.email}
                onChange={(e) => onUpdate(worker.id, { email: e.target.value })}
                placeholder="Email"
                error={dayWorkerEmailError(worker.email)}
              />
            </div>
          </div>
        ))}
      </div>

      <ButtonSmall onClick={onAdd}>+ Add Day Worker</ButtonSmall>
    </div>
  );
}
